// The geometry behind the Core Orientation 3D chart: the hole drawn as a tube,
// with each core run a cylinder inside it - green where the orientation is good,
// red where the run is bad. Pure, so every rule here is testable without WebGL.
//
// Everything is in the chart's frame, [east, north, -TVD], with the collar at
// the origin (see withCollarOrigin in coreOrientation.js).

import { orderedPositions } from './surveyPositions';
import { storedQaPass, withCollarOrigin } from './coreOrientation';

// The longest core a single run can have produced: one barrel. A run's section
// reaches back to the previous run's depth, but never further than this, so a
// stretch nobody oriented reads as plain hole rather than as one long core.
export const MAX_CORE_RUN_METRES = 3;

// The composite's approved shots, collar first, as the path everything is
// placed on.
export const holeTrace = (composite) =>
    withCollarOrigin(orderedPositions(composite || {})).lines;

// One section per run, deepest last. Approved and rejected runs are merged: a
// rejected run is still a core that was drilled, and it is the bad-run case the
// chart exists to show. The composite lists each group in shot-time order, and
// the sort is stable, so runs at one depth stay earliest first within a group.
export const coreSections = (composite, maxRunMetres = MAX_CORE_RUN_METRES) => {
    const approved = (composite?.SavedShots || []).map(shot => ({ shot, rejected: false }));
    const rejected = (composite?.RejectedShots || []).map(shot => ({ shot, rejected: true }));
    const runs = [...approved, ...rejected]
        .filter(({ shot }) => typeof shot.Depth === 'number' && Number.isFinite(shot.Depth))
        .sort((a, b) => a.shot.Depth - b.shot.Depth);

    let previous = 0;
    return runs.map(({ shot, rejected: isRejected }) => {
        const bottom = shot.Depth;
        const top = Math.min(bottom, Math.max(previous, bottom - maxRunMetres));
        previous = bottom;
        const verdict = storedQaPass(shot.OrientationSet);
        return {
            fileId: shot.SourceFileId,
            surveyName: shot.SourceSurveyName || '',
            top,
            bottom,
            status: isRejected ? 'rejected' : verdict === false ? 'fail' : verdict === true ? 'pass' : 'unchecked'
        };
    });
};

const toPoint = (p) => [p.easting, p.northing, 0 - p.tvd];
const lerp = (a, b, f) => a.map((value, i) => value + (b[i] - value) * f);

// Where a depth sits on the trace, in chart coordinates. Linear between the
// stations either side; past the deepest station it carries on along the last
// leg, because a rejected run can be deeper than every approved one. Null when
// the trace is too short to give a direction.
export const pointAtDepth = (trace, depth) => {
    if (!trace || trace.length < 2) return null;
    let i = 1;
    while (i < trace.length - 1 && trace[i].depth < depth) i++;
    const a = trace[i - 1];
    const b = trace[i];
    const span = b.depth - a.depth;
    const f = span === 0 ? 0 : (depth - a.depth) / span;
    return lerp(toPoint(a), toPoint(b), f);
};

// Straight pieces between two depths, broken at every station in between, so a
// section that crosses a bend follows the path instead of cutting the corner.
const piecesBetween = (trace, top, bottom) => {
    if (bottom <= top) return [];
    const depths = [top, ...trace.map(p => p.depth).filter(d => d > top && d < bottom), bottom];
    const pieces = [];
    for (let i = 1; i < depths.length; i++) {
        const from = pointAtDepth(trace, depths[i - 1]);
        const to = pointAtDepth(trace, depths[i]);
        if (from && to) pieces.push({ from, to });
    }
    return pieces;
};

// Every tube the chart draws: the hole from the collar to the deepest thing on
// it, then one or more pieces per core section, carrying the section's status.
export const tubesFor = (trace, sections) => {
    if (!trace || trace.length < 2) return [];
    const deepest = Math.max(trace[trace.length - 1].depth, ...sections.map(s => s.bottom));
    const hole = piecesBetween(trace, 0, deepest).map(piece => ({ ...piece, kind: 'hole' }));
    const cores = sections.flatMap(section =>
        piecesBetween(trace, section.top, section.bottom).map(piece => ({
            ...piece, kind: 'core', status: section.status, fileId: section.fileId
        })));
    return [...hole, ...cores];
};

const sub = (a, b) => a.map((v, i) => v - b[i]);
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const length = (a) => Math.hypot(...a);
const scale = (a, s) => a.map(v => v * s);

// Two unit vectors perpendicular to the tube's axis and to each other. The
// reference axis is whichever of east or up is less parallel to the tube, so a
// vertical hole - the common case near the collar - never degenerates.
export const tubeBasis = (from, to) => {
    const axis = sub(to, from);
    const len = length(axis);
    if (len === 0) return null;
    const d = scale(axis, 1 / len);
    const reference = Math.abs(d[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
    const u = cross(d, reference);
    const U = scale(u, 1 / length(u));
    const W = cross(d, U);
    return { U, W };
};

// The parametric equation echarts-gl's surface series takes: a straight
// cylinder of the given radius from `from` to `to`, u around it and v along it.
export const tubeSurface = (from, to, radius) => {
    const basis = tubeBasis(from, to);
    if (!basis) return null;
    const { U, W } = basis;
    const d = sub(to, from);
    const at = (axis) => (u, v) => from[axis] + v * d[axis] + radius * (Math.cos(u) * U[axis] + Math.sin(u) * W[axis]);
    return {
        u: { min: 0, max: 2 * Math.PI, step: Math.PI / 12 },
        v: { min: 0, max: 1, step: 1 },
        x: at(0),
        y: at(1),
        z: at(2)
    };
};
